import { SeededRandom } from './SeededRandom.js';
import { ZONE } from './ZoneTypes.js';

/** Semver version of the TMJ output contract. See docs for schema details.
 *  Patch: new optional fields. Minor: new required layers. Major: breaking changes. */
export const TMJ_OUTPUT_VERSION = '1.0.0';

/**
 * Base class for all map generators.
 * Provides grid utilities, TMJ building, and common helpers.
 */
export class BaseGenerator {
  constructor(options = {}) {
    this.width = options.width || 40;
    this.height = options.height || 30;
    this.tileWidth = options.tileWidth || 32;
    this.tileHeight = options.tileHeight || 32;
    this.seed = options.seed || Date.now();
    this.rng = new SeededRandom(this.seed);
    this.options = options;
  }

  /** Create a 2D grid filled with a value. */
  createGrid(width, height, fillValue = 0) {
    const grid = [];
    for (let y = 0; y < height; y++) {
      grid.push(new Array(width).fill(fillValue));
    }
    return grid;
  }

  /** Flatten 2D grid to 1D array (row-major). */
  flattenGrid(grid) {
    const result = [];
    for (const row of grid) {
      result.push(...row);
    }
    return result;
  }

  /** Get value from 2D grid with bounds checking. */
  getCell(grid, x, y) {
    if (x < 0 || y < 0 || y >= grid.length || x >= grid[0].length) return -1;
    return grid[y][x];
  }

  /** Set value in 2D grid with bounds checking. */
  setCell(grid, x, y, value) {
    if (x < 0 || y < 0 || y >= grid.length || x >= grid[0].length) return;
    grid[y][x] = value;
  }

  /** Get 4-directional neighbors. */
  getNeighbors4(x, y) {
    return [
      { x: x - 1, y, dir: 'west' },
      { x: x + 1, y, dir: 'east' },
      { x, y: y - 1, dir: 'north' },
      { x, y: y + 1, dir: 'south' },
    ];
  }

  /** Get 8-directional neighbors. */
  getNeighbors8(x, y) {
    return [
      ...this.getNeighbors4(x, y),
      { x: x - 1, y: y - 1, dir: 'northwest' },
      { x: x + 1, y: y - 1, dir: 'northeast' },
      { x: x - 1, y: y + 1, dir: 'southwest' },
      { x: x + 1, y: y + 1, dir: 'southeast' },
    ];
  }

  /** Count neighbors matching a predicate. */
  countNeighbors(grid, x, y, predicate, diagonal = false) {
    const neighbors = diagonal ? this.getNeighbors8(x, y) : this.getNeighbors4(x, y);
    let count = 0;
    for (const n of neighbors) {
      const val = this.getCell(grid, n.x, n.y);
      if (val !== -1 && predicate(val)) count++;
    }
    return count;
  }

  /** Fill a rectangular area in a grid. */
  fillRect(grid, x1, y1, x2, y2, value) {
    for (let y = y1; y <= y2; y++) {
      for (let x = x1; x <= x2; x++) {
        this.setCell(grid, x, y, value);
      }
    }
  }

  /** Draw a line between two points using Bresenham's algorithm. */
  drawLine(grid, x0, y0, x1, y1, value, thickness = 1) {
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    let cx = x0, cy = y0;

    while (true) {
      // Apply thickness
      const half = Math.floor(thickness / 2);
      for (let ty = -half; ty <= half; ty++) {
        for (let tx = -half; tx <= half; tx++) {
          this.setCell(grid, cx + tx, cy + ty, value);
        }
      }
      if (cx === x1 && cy === y1) break;
      const e2 = 2 * err;
      if (e2 > -dy) { err -= dy; cx += sx; }
      if (e2 < dx) { err += dx; cy += sy; }
    }
  }

  /** Flood fill on grid. Returns number of cells filled. */
  floodFill(grid, x, y, newValue, targetValue = undefined) {
    if (targetValue === undefined) targetValue = this.getCell(grid, x, y);
    if (targetValue === newValue || targetValue === -1) return 0;

    const queue = [[x, y]];
    let count = 0;
    const w = grid[0].length;
    const h = grid.length;

    while (queue.length > 0) {
      const [cx, cy] = queue.pop();
      if (cx < 0 || cy < 0 || cx >= w || cy >= h) continue;
      if (grid[cy][cx] !== targetValue) continue;
      grid[cy][cx] = newValue;
      count++;
      queue.push([cx - 1, cy], [cx + 1, cy], [cx, cy - 1], [cx, cy + 1]);
    }
    return count;
  }

  /**
   * Build a TMJ-compatible map object from layers.
   * @param {Object} config - { layers: [{ name, data }], tilesets, properties, objects }
   */
  buildTMJ(config) {
    const layers = config.layers.map((layer, i) => {
      if (layer.type === 'objectgroup') {
        return {
          draworder: 'topdown',
          id: i + 1,
          name: layer.name,
          objects: layer.objects || [],
          opacity: 1,
          type: 'objectgroup',
          visible: true,
          x: 0,
          y: 0,
        };
      }
      return {
        data: layer.data instanceof Array ? layer.data : Array.from(layer.data),
        height: this.height,
        id: i + 1,
        name: layer.name,
        opacity: 1,
        type: 'tilelayer',
        visible: true,
        width: this.width,
        x: 0,
        y: 0,
      };
    });

    return {
      compressionlevel: -1,
      height: this.height,
      infinite: false,
      layers,
      nextlayerid: layers.length + 1,
      nextobjectid: 1,
      orientation: 'orthogonal',
      renderorder: 'right-down',
      tiledversion: '1.11.0',
      tileheight: this.tileHeight,
      tilesets: config.tilesets || [],
      tilewidth: this.tileWidth,
      type: 'map',
      version: '1.10',
      width: this.width,
      properties: config.properties,
      tileforge: {
        version: TMJ_OUTPUT_VERSION,
        generator: this.constructor.name,
        seed: this.seed,
        timestamp: Date.now(),
      },
    };
  }

  /**
   * Override in subclasses. Returns a TMJ-compatible map object.
   * @param {object} content - Content provider data (tilesets, profiles, etc.)
   * @returns {object} TMJ map data
   */
  generate(content) {
    throw new Error('Subclass must implement generate()');
  }

  /**
   * Override in subclasses. Declares which ZONE constants this generator emits.
   * Used by CompatibilityChecker to verify TileMeta can resolve all zones.
   * @returns {number[]} Array of ZONE constant values
   */
  /** Add natural-looking terrain variation patches to grass areas. */
  _addGrassVariation(ground, patchCount = 5) {
    for (let i = 0; i < patchCount; i++) {
      const cx = this.rng.nextInt(2, this.width - 3);
      const cy = this.rng.nextInt(2, this.height - 3);
      const radius = this.rng.nextInt(2, 5);
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          if (dx * dx + dy * dy > radius * radius) continue;
          if (this.rng.chance(0.3)) continue;
          const x = cx + dx, y = cy + dy;
          if (x < 0 || y < 0 || x >= this.width || y >= this.height) continue;
          if (this.getCell(ground, x, y) === ZONE.GRASS) {
            this.setCell(ground, x, y, ZONE.DIRT);
          }
        }
      }
    }
  }

  static getEmittedZones() { return []; }

  /**
   * Redistribute tile IDs across layers based on tileLayers metadata.
   *
   * After zone resolution, tiles may be on the wrong layer (e.g. a rug tile
   * resolved onto the objects grid but tagged as 'ground' in the tagger).
   * This method moves tile IDs from their current grid to the correct one.
   *
   * @param {Object} grids — { ground: 2D[], objects: 2D[], overhead: 2D[] }
   * @param {Object} tileLayers — { [tileId]: 'ground'|'objects'|'overhead' }
   * @returns {Object} Same grids reference, mutated in place
   */
  redistributeByLayer(grids, tileLayers) {
    if (!tileLayers || Object.keys(tileLayers).length === 0) return grids;

    const layerGrids = {
      ground: grids.ground,
      objects: grids.objects,
      overhead: grids.overhead,
    };
    const gridNames = ['ground', 'objects', 'overhead'];

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        for (const srcName of gridNames) {
          const srcGrid = layerGrids[srcName];
          if (!srcGrid) continue;
          const tileId = srcGrid[y][x];
          if (tileId === 0) continue;

          const targetLayer = tileLayers[tileId];
          if (!targetLayer || targetLayer === srcName) continue;

          const dstGrid = layerGrids[targetLayer];
          if (!dstGrid) continue;

          // Only move if destination cell is empty (don't overwrite existing tiles)
          if (dstGrid[y][x] === 0) {
            dstGrid[y][x] = tileId;
            srcGrid[y][x] = 0;
          }
        }
      }
    }

    return grids;
  }
}
